import { useCallback, useMemo } from 'react';

import { useSelector } from '@hh.ru/front-static-app';
import { VSpacing, CustomChip, ChipsContainer } from '@hh.ru/magritte-ui';
import { PlusOutlinedSize16 } from '@hh.ru/magritte-ui/icon';
import { TranslatedComponent } from 'bloko/common/hooks/useTranslations';
import { format } from 'bloko/common/trl';

import { useApplyFilter } from 'src/components/Search/Common/Filters/hooks/useApplyFilter';
import MetroIcon from 'src/components/Search/Common/Metro/MetroIcon';
import { useGeoFiltersAnalytics } from 'src/components/Search/Resume/Filters/Geo/hooks/useAnalytics';
import { useGeoFiltersMetroRedesignExp } from 'src/components/Search/Resume/Filters/Geo/hooks/useGeoFiltersRedesignExp';
import { useOpenGeoDrawer } from 'src/components/Search/Resume/Filters/Geo/hooks/useOpenGeoDrawer';
import translation from 'src/components/translation';
import useToggleState from 'src/hooks/useToggleState';
import { CurrentGeoFilter } from 'src/models/search/common/geoDrawer';
import { ClusterKey, MetroType, SearchVacancyMetroClusterGroup } from 'src/types/search/common/clusters';

import { Chips } from 'src/components/Search/Resume/Filters/Geo/HeaderChips/types';
import { uncheckChips } from 'src/components/Search/Resume/Filters/Geo/HeaderChips/utils';

const MAX_ITEMS_TO_RENDER = 5;
const HHTM_SOURCE_LABEL = 'search_header';

const TrlKeys = {
    moreButtonText: 'search.resume.filters.geo.headerChips.moreButton.text',
    filters: {
        [ClusterKey.District]: 'clusters.district',
        [ClusterKey.Metro]: 'search.resume.filters.geo.metro.title',
    },
};

const HeaderChips: TranslatedComponent = ({ trls }) => {
    const openDraftForm = useOpenGeoDrawer();
    const applyAreaFilter = useApplyFilter(ClusterKey.Area);
    const applyMetroFilter = useApplyFilter(ClusterKey.Metro);
    const applyDistrictFilter = useApplyFilter(ClusterKey.District);
    const areaCluster = useSelector((state) => state.searchClusters[ClusterKey.Area]);
    const metroCluster = useSelector((state) => state.searchClusters[ClusterKey.Metro]);
    const districtCluster = useSelector((state) => state.searchClusters[ClusterKey.District]);
    const metroCities = useSelector((state) => state.metroCities);
    const citiesDistricts = useSelector((state) => state.citiesDistricts);

    const isGeoFiltersMetroRedesignExp = useGeoFiltersMetroRedesignExp();

    const { sendEventGeoDraftOpen, sendEventDeleteHeaderChip } = useGeoFiltersAnalytics();

    const handleAddMetroClick = useCallback(() => {
        openDraftForm(CurrentGeoFilter.METRO);
        sendEventGeoDraftOpen(HHTM_SOURCE_LABEL);
    }, [openDraftForm, sendEventGeoDraftOpen]);

    const handleAddDistrictClick = useCallback(() => {
        openDraftForm(CurrentGeoFilter.DISTRICT);
        sendEventGeoDraftOpen(HHTM_SOURCE_LABEL);
    }, [openDraftForm, sendEventGeoDraftOpen]);

    const allChips = useMemo(() => {
        const chips: Chips[] = [];

        if (areaCluster) {
            const { groups, selectedValues } = areaCluster;

            selectedValues.forEach((selectedId: number) => {
                chips.push({
                    type: ClusterKey.Area,
                    group: groups[selectedId],
                    selectedValues,
                    deleteHandler: applyAreaFilter as Chips['deleteHandler'],
                });
            });
        }

        if (isGeoFiltersMetroRedesignExp && metroCluster) {
            const { groups, selectedValues } = metroCluster;

            selectedValues.forEach((selectedId: string) => {
                chips.push({
                    type: ClusterKey.Metro,
                    group: groups[String(selectedId)],
                    selectedValues,
                    deleteHandler: applyMetroFilter as Chips['deleteHandler'],
                });
            });
        }

        if (districtCluster) {
            const { groups, selectedValues } = districtCluster;

            selectedValues.forEach((selectedId: number) => {
                chips.push({
                    type: ClusterKey.District,
                    group: groups[String(selectedId)],
                    selectedValues,
                    deleteHandler: applyDistrictFilter as Chips['deleteHandler'],
                });
            });
        }

        return chips;
    }, [
        isGeoFiltersMetroRedesignExp,
        areaCluster,
        metroCluster,
        districtCluster,
        applyAreaFilter,
        applyMetroFilter,
        applyDistrictFilter,
    ]);

    const [shouldRenderAll, toggleShouldRenderAll] = useToggleState(false);

    const chipsToRender = useMemo(() => {
        return shouldRenderAll ? allChips : allChips.slice(0, MAX_ITEMS_TO_RENDER);
    }, [allChips, shouldRenderAll]);
    const itemsLeftToRender = allChips.length - MAX_ITEMS_TO_RENDER;

    const showOpenMetroFilterChip = useMemo(
        () =>
            isGeoFiltersMetroRedesignExp &&
            areaCluster?.selectedValues?.length === 1 &&
            metroCities.includes(String(areaCluster.selectedValues[0])) &&
            metroCluster?.selectedValues?.length === 0,
        [isGeoFiltersMetroRedesignExp, areaCluster?.selectedValues, metroCluster?.selectedValues, metroCities]
    );

    const showOpenDistrictFilterChip = useMemo(
        () =>
            areaCluster?.selectedValues?.length === 1 &&
            areaCluster.selectedValues[0] in citiesDistricts &&
            districtCluster?.selectedValues?.length === 0,
        [areaCluster, citiesDistricts, districtCluster]
    );

    return (
        <>
            <VSpacing default={12} />
            <ChipsContainer>
                {chipsToRender.map((item) => {
                    const { type, selectedValues, deleteHandler } = item;
                    const group = item.group as SearchVacancyMetroClusterGroup;
                    const isMetroGroup = type === ClusterKey.Metro;

                    return (
                        <CustomChip
                            key={`${group.id}-${group.title}`}
                            size="small"
                            Element="button"
                            onClick={() => {
                                if (type === ClusterKey.Area) {
                                    openDraftForm(CurrentGeoFilter.AREA);
                                } else if (type === ClusterKey.Metro) {
                                    openDraftForm(CurrentGeoFilter.METRO);
                                } else if (type === ClusterKey.District) {
                                    openDraftForm(CurrentGeoFilter.DISTRICT);
                                }
                                sendEventGeoDraftOpen(HHTM_SOURCE_LABEL);
                            }}
                            onDelete={() => {
                                deleteHandler(uncheckChips(selectedValues, group.id));

                                if (type === ClusterKey.Area) {
                                    applyMetroFilter([]);
                                    applyDistrictFilter([]);
                                }
                                sendEventDeleteHeaderChip(type, group.id);
                            }}
                            addon={
                                isMetroGroup ? (
                                    <MetroIcon isLine={group?.type === MetroType.Line} color={group?.color} />
                                ) : undefined
                            }
                        >
                            {group.title}
                        </CustomChip>
                    );
                })}
                {!shouldRenderAll && allChips.length > MAX_ITEMS_TO_RENDER && (
                    <CustomChip size="small" Element="button" onClick={toggleShouldRenderAll}>
                        {format(trls[TrlKeys.moreButtonText], { '{counter}': itemsLeftToRender })}
                    </CustomChip>
                )}
                {showOpenMetroFilterChip && (
                    <CustomChip
                        size="small"
                        style="accent"
                        Element="button"
                        onClick={handleAddMetroClick}
                        addon={<PlusOutlinedSize16 />}
                    >
                        {trls[TrlKeys.filters.metro]}
                    </CustomChip>
                )}
                {showOpenDistrictFilterChip && (
                    <CustomChip
                        size="small"
                        style="accent"
                        Element="button"
                        onClick={handleAddDistrictClick}
                        addon={<PlusOutlinedSize16 />}
                    >
                        {trls[TrlKeys.filters.district]}
                    </CustomChip>
                )}
            </ChipsContainer>
        </>
    );
};

export default translation(HeaderChips);
